import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminFetchGalleryAlbumBySlug, ApiError, fetchGalleryAlbum } from "../api/client";
import type { GalleryAlbumDetail } from "@tih/shared";
import { GalleryMasonry } from "../components/GalleryMasonry";
import { useReveal } from "../hooks/useReveal";

function formatEventDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function GalleryAlbumPage() {
  const { slug } = useParams<{ slug: string }>();
  const ref = useReveal<HTMLElement>();
  const [album, setAlbum] = useState<GalleryAlbumDetail | null>(null);
  const [draftPreview, setDraftPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDraftPreview(false);

    void (async () => {
      try {
        const data = await fetchGalleryAlbum(slug);
        if (!cancelled) setAlbum(data);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          try {
            const data = await adminFetchGalleryAlbumBySlug(slug);
            if (!cancelled) {
              setAlbum(data);
              setDraftPreview(true);
            }
            return;
          } catch {
            // fall through to public error
          }
        }
        setError(String((e as Error).message ?? e));
        setAlbum(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <section className="section section-gallery-album" ref={ref}>
      <nav className="gal-breadcrumb">
        <Link to="/gallery">← All albums</Link>
      </nav>

      {loading ? (
        <div className="gal-empty">Loading album…</div>
      ) : error || !album ? (
        <div className="gal-empty gal-error">
          <p>{error ?? "Album not found."}</p>
          <Link to="/gallery" className="btn btn-ghost">
            Back to gallery
          </Link>
        </div>
      ) : (
        <>
          {draftPreview && (
            <p className="gal-album-draft-banner" role="status">
              Draft preview — this album is not published yet. Visitors will not see it until you
              publish from the event album admin.
            </p>
          )}
          <header className="gal-album-header">
            <h1 className="sec-title">{album.title}</h1>
            {album.eventDate && (
              <time className="gal-album-date" dateTime={album.eventDate}>
                {formatEventDate(album.eventDate)}
              </time>
            )}
            {album.caption && <p className="gal-album-desc">{album.caption}</p>}
            <p className="gal-album-stats">
              {album.photoCount} {album.photoCount === 1 ? "photo" : "photos"}
            </p>
          </header>
          <GalleryMasonry photos={album.photos} />
        </>
      )}
    </section>
  );
}
